import type { ReactNode } from 'react';
import { SmilePlus } from 'lucide-react';
import { cn } from './lib/cn';

export interface ReactionPillProps {
  /**
   * The emoji character, e.g. "👍", or a `:shortcode:` for tenant custom emoji.
   * Always the accessible label, even when `glyph` supplies the visuals.
   */
  emoji: string;
  /**
   * Rendered in place of `emoji` when the token is not a printable character —
   * a custom emoji is an image, and this primitive must not know how to resolve
   * one.
   */
  glyph?: ReactNode;
  count: number;
  /** Highlights the pill in brand purple when the current user reacted. */
  reacted?: boolean;
  onToggle?: () => void;
  /** Names for the tooltip/aria label, e.g. ["Rohit Sharma", "Anjali Mehta"]. */
  reactors?: readonly string[];
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE = {
  sm: 'h-6 gap-1 px-2 text-3xs',
  md: 'h-7 gap-1.5 px-2.5 text-sm',
} as const;

const EMOJI_SIZE = { sm: 'text-xs', md: 'text-md' } as const;

/** Emoji + count chip under a post or chat bubble. Rounded-full, hairline border. */
export function ReactionPill({
  emoji, glyph, count, reacted = false, onToggle, reactors, size = 'md', className,
}: ReactionPillProps) {
  const label = reactors?.length
    ? `${emoji} ${count} — ${reactors.slice(0, 3).join(', ')}`
    : `${emoji} ${count} reactions`;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={reacted}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center rounded-pill border font-semibold tabular transition-colors duration-fast',
        SIZE[size],
        reacted
          ? 'border-primary/40 bg-primary-subtle text-primary'
          : 'border-border bg-card text-secondary-foreground hover:border-border-strong hover:bg-subtle',
        className,
      )}
    >
      <span
        className={cn('inline-flex items-center leading-none', EMOJI_SIZE[size])}
        aria-hidden
      >
        {glyph ?? emoji}
      </span>
      {count}
    </button>
  );
}

export interface AddReactionButtonProps {
  onClick?: () => void;
  size?: 'sm' | 'md';
  className?: string;
}

/** The trailing "+" affordance that opens the emoji picker. */
export function AddReactionButton({ onClick, size = 'md', className }: AddReactionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add a reaction"
      title="Add a reaction"
      className={cn(
        'inline-flex items-center justify-center rounded-pill border border-dashed border-border text-muted-foreground transition-colors duration-fast hover:border-primary/40 hover:bg-primary-subtle hover:text-primary',
        size === 'sm' ? 'h-6 w-6' : 'h-7 w-7',
        className,
      )}
    >
      <SmilePlus size={size === 'sm' ? 12 : 14} strokeWidth={2} aria-hidden />
    </button>
  );
}
